import { CustomArrowFilled } from '@qr-front/icons/custom-arrow-filled'
import { LinePlus } from '@qr-front/icons/line-plus'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@qr-front/ui/components/Accordion'
import { Button } from '@qr-front/ui/components/Button'
import { cn } from '@qr-front/ui/utils'
import {
	DESCRIPTION_MAX_LENGTH,
	NAME_MAX_LENGTH,
} from 'constants/validationConstants'
import { RhcField } from 'features/generator/components/RhcField'
import { PrimitiveAtom, useAtom, useSetAtom } from 'jotai'
import { useTranslations } from 'next-intl'
import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { useBus, useListener } from 'react-bus'
import { useFormContext } from 'react-hook-form'
import { TUploadedMedia } from 'services/mediaStorage'
import { maxLength } from 'utils/inputChangeUtils'

import { GENERATOR_FORM_ANIMATION_DURATION } from '../../../components/GeneratorFormSection'
import {
	CLOSE_MENU_SECTION_ACCORDIONS_EVENT,
	OPEN_MENU_SECTION_ACCORDION_EVENT,
	sectionsListAtom,
	showSectionsSortDialogAtom,
	TMenuSection,
	TMenuSectionProduct,
} from '../MenuForm'
import { TMenuFormFields } from '../menuFormModel'
import { Actions } from './Actions'
import { Product, TProductHandle } from './Product'
import { SectionDeleteModal } from './SectionDeleteModal'

export const CLOSE_MENU_PRODUCT_ACCORDIONS_EVENT =
	'CLOSE_MENU_PRODUCT_ACCORDIONS_EVENT'

const emptyImage = {
	publicId: '',
	resourceType: '',
	format: '',
	bytes: 0,
} as TUploadedMedia

export const emptyProduct: TMenuSectionProduct = {
	id: 1,
	image: emptyImage,
	name: '',
	nameTranslation: '',
	description: '',
	descriptionTranslation: '',
	price: '',
	allergens: [],
	visible: true,
}

export type TMenuSectionHandle = {
	scrollIntoView: () => void
}

export const Section = forwardRef<
	TMenuSectionHandle,
	{
		sectionAtom: PrimitiveAtom<TMenuSection>
		index: number
		onProductsReorder: (
			products: TMenuSectionProduct[],
			sectionIndex: number,
		) => void
	}
>(({ sectionAtom, index: sectionIndex, onProductsReorder }, ref) => {
	const t = useTranslations()

	const containerRef = useRef<HTMLDivElement>(null)

	const productIndexToHandleRef = useRef<Record<number, TProductHandle>>({})

	useImperativeHandle(ref, () => ({
		scrollIntoView: () => {
			containerRef.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			})
		},
	}))

	const defaultAccordionValue = `section-${sectionIndex}`
	const [accordionValue, setAccordionValue] = useState<string>(
		defaultAccordionValue,
	)
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const bus = useBus()

	const form = useFormContext<TMenuFormFields>()
	const [sectionsList, setSectionsList] = useAtom(sectionsListAtom)
	const [section, setSection] = useAtom(sectionAtom)

	const setShowSortDialog = useSetAtom(showSectionsSortDialogAtom)

	const { getValues, setFocus } = form

	const {
		description,
		descriptionTranslation,
		name,
		nameTranslation,
		visible,
		products,
		id,
	} = section

	const handleSetValueForField =
		(field: keyof TMenuSection) => (value?: string) => {
			setSection((section) => ({
				...section,
				[field]: value || '',
			}))
		}

	const handleOpenAccordion = () => {
		setAccordionValue(defaultAccordionValue)
	}

	const handleProductAdd = () => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur()
		}

		setSection((section) => ({
			...section,
			products: [
				...section.products,
				{ ...emptyProduct, id: section.products.length + 1 },
			],
		}))

		bus.emit(CLOSE_MENU_PRODUCT_ACCORDIONS_EVENT)

		const sections = getValues('sections')
		if (!sections?.[sectionIndex]) {
			return
		}

		const products = sections[sectionIndex]?.products ?? []
		const newProductIndex = products.length
		const newProductNamePath =
			`sections.${sectionIndex}.products.${newProductIndex}.name` as const
		setTimeout(() => {
			setFocus(newProductNamePath)
			productIndexToHandleRef.current[newProductIndex]?.scrollIntoView()
		}, GENERATOR_FORM_ANIMATION_DURATION * 1.25)
	}

	const handleSectionHide = () => {
		setAccordionValue('')
		setSection((section) => ({
			...section,
			visible: !section.visible,
		}))
	}

	const handleSectionDelete = () => {
		setShowDeleteModal(true)
	}

	const handleSectionDeleteCancel = () => {
		setShowDeleteModal(false)
	}

	const handleSectionReorder = () => {
		setShowSortDialog(true)
	}

	const handleProductsReorder = () => {
		onProductsReorder(products, sectionIndex)
	}

	const handleSectionDeleteConfirm = () => {
		setSectionsList((sections) =>
			sections.filter((_, i) => i !== sectionIndex),
		)
		setShowDeleteModal(false)
		form.trigger('sections')
	}

	const handleCloseAccordion = () => setAccordionValue('')

	useListener(CLOSE_MENU_SECTION_ACCORDIONS_EVENT, handleCloseAccordion)

	useListener(OPEN_MENU_SECTION_ACCORDION_EVENT, (event: unknown) => {
		const data = event as { sectionIndex?: number }
		if (data?.sectionIndex === sectionIndex) {
			setAccordionValue(defaultAccordionValue)
		}
	})

	return (
		<div
			ref={containerRef}
			className="flex scroll-mt-22 flex-col gap-6 tablet:scroll-mt-24 tablet:gap-8 desktop:scroll-mt-26"
		>
			<Accordion
				type="single"
				collapsible
				value={accordionValue}
				onValueChange={setAccordionValue}
				disabled={!visible}
			>
				<AccordionItem
					value={accordionValue}
					className="flex flex-col gap-8"
				>
					<div className="flex flex-col gap-4">
						<div className="flex w-full items-center justify-between gap-2">
							<div className="min-w-0 flex-1">
								<AccordionTrigger
									iconPosition="left"
									icon={CustomArrowFilled}
									onClick={handleOpenAccordion}
									id={defaultAccordionValue}
									className={cn(
										'flex w-full items-center justify-start gap-2 p-0',
										'[&[data-state=closed]>svg]:-rotate-90 [&[data-state=open]>svg]:rotate-0',
										!visible && 'opacity-20',
									)}
								>
									<span className="block max-w-full overflow-hidden truncate text-ellipsis whitespace-nowrap">
										{name ||
											`${t('generator__content_form_section__menu__sections__default_name')} ${id}`}
									</span>
								</AccordionTrigger>
							</div>

							<Actions
								category="section"
								disabled={!visible}
								showReorderButton={sectionsList.length > 1}
								onDelete={handleSectionDelete}
								onHide={handleSectionHide}
								onReorder={handleSectionReorder}
							/>
						</div>
						{!visible && (
							<div className="flex items-center justify-center gap-2 rounded-full border border-dashed border-border-grey px-4 py-2 tablet:hidden">
								<span className="type-body-caption text-dark-grey">
									{t(
										'generator__content_form_section__menu__hidden_label',
									)}
								</span>
							</div>
						)}
					</div>

					<AccordionContent className="flex flex-col gap-6 p-0 tablet:gap-8">
						<div className="grid grid-cols-1 gap-x-12 gap-y-4 tablet:grid-cols-2">
							<RhcField
								required
								type="text"
								form={form}
								path={`sections.${sectionIndex}.name`}
								value={name}
								setValue={handleSetValueForField('name')}
								fieldName="menu_section_name"
								fieldGroup="menu_section"
								onChangeTransform={(event) =>
									maxLength(NAME_MAX_LENGTH)(event)
								}
								label={t(
									'generator__content_form_section__menu__sections__name_label',
								)}
								placeholder={t(
									'generator__content_form_section__menu__sections__name_placeholder',
								)}
								className="w-full tablet:grow"
							/>

							<RhcField
								type="text"
								form={form}
								path={`sections.${sectionIndex}.nameTranslation`}
								value={nameTranslation}
								setValue={handleSetValueForField(
									'nameTranslation',
								)}
								fieldName="menu_section_name_translation"
								fieldGroup="menu_section"
								onChangeTransform={(event) =>
									maxLength(NAME_MAX_LENGTH)(event)
								}
								label={t(
									'generator__content_form_section__menu__sections__translation_label',
								)}
								placeholder={t(
									'generator__content_form_section__menu__sections__translation_placeholder',
								)}
							/>

							<RhcField
								type="text"
								form={form}
								path={`sections.${sectionIndex}.description`}
								value={description}
								setValue={handleSetValueForField('description')}
								fieldName="menu_section_description"
								fieldGroup="menu_section"
								onChangeTransform={(event) =>
									maxLength(DESCRIPTION_MAX_LENGTH)(event)
								}
								label={t(
									'generator__content_form_section__menu__sections__description_label',
								)}
								placeholder={t(
									'generator__content_form_section__menu__sections__description_placeholder',
								)}
							/>

							<RhcField
								type="text"
								form={form}
								path={`sections.${sectionIndex}.descriptionTranslation`}
								value={descriptionTranslation}
								setValue={handleSetValueForField(
									'descriptionTranslation',
								)}
								fieldName="menu_section_description_translation"
								fieldGroup="menu_section"
								onChangeTransform={(event) =>
									maxLength(DESCRIPTION_MAX_LENGTH)(event)
								}
								label={t(
									'generator__content_form_section__menu__sections__translation_description_label',
								)}
								placeholder={t(
									'generator__content_form_section__menu__sections__translation_description_placeholder',
								)}
							/>
						</div>
						<div className="flex flex-col gap-2">
							{products.map((_, productIndex) => (
								<Product
									key={productIndex}
									ref={(ref) => {
										if (ref) {
											productIndexToHandleRef.current[
												productIndex
											] = ref
										}
									}}
									index={productIndex}
									sectionAtom={sectionAtom}
									sectionIndex={sectionIndex}
									onProductsReorderClick={
										handleProductsReorder
									}
								/>
							))}
							<div className="flex flex-col items-start justify-center gap-2 rounded-10 bg-light-grey-80 p-4 tablet:p-6">
								<Button
									visual="soft"
									className="w-fit"
									onClick={handleProductAdd}
								>
									<LinePlus width={16} height={16} />
									{products.length > 0
										? t(
												'generator__content_form_section__menu__sections__add_new_product',
											)
										: t(
												'generator__content_form_section__menu__sections__add_product',
											)}
								</Button>
							</div>
						</div>
					</AccordionContent>
				</AccordionItem>
			</Accordion>

			<div className="divider h-0.5 w-full bg-border-grey/50" />

			<SectionDeleteModal
				isOpen={showDeleteModal}
				onCancel={handleSectionDeleteCancel}
				onDelete={handleSectionDeleteConfirm}
			/>
		</div>
	)
})

Section.displayName = 'Section'
